<template>
  <v-dialog
    v-model="dialog"
    max-width="800px"
    scrollable
    @click:outside="closeDialog"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Trips for {{ boatName }}</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="max-height: 600px; padding: 20px">
        <div v-if="loading" class="text-center py-5">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </div>

        <div v-else-if="groupedTrips.length === 0" class="text-center py-5">
          <v-icon size="64" color="grey lighten-1">mdi-ferry</v-icon>
          <p class="mt-3 grey--text">No trips found for this boat</p>
        </div>

        <div v-else>
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <div class="d-flex" style="gap: 8px">
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="selectAll"
                  :disabled="selectedTrips.length === totalTripsCount"
                >
                  Select All
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="success"
                  @click="selectUnbooked"
                  :disabled="unbookedTripsCount === 0"
                >
                  Select Unbooked
                </v-btn>
              </div>
              <span class="caption grey--text">
                {{ selectedTrips.length }} of {{ totalTripsCount }} selected
              </span>
              <div class="d-flex" style="gap: 8px">
                <v-btn
                  small
                  outlined
                  color="warning"
                  @click="deselectBooked"
                  :disabled="selectedTrips.length === 0"
                >
                  Deselect Booked
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="secondary"
                  @click="deselectAll"
                  :disabled="selectedTrips.length === 0"
                >
                  Deselect All
                </v-btn>
              </div>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div v-for="(group, index) in groupedTrips" :key="index" class="mb-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <h3 class="subtitle-1 font-weight-bold">
                {{ group.date }}
              </h3>
              <v-checkbox
                v-model="group.allSelected"
                @change="toggleGroupSelection(group)"
                hide-details
                dense
                class="mt-0"
                label="Select all for this date"
              ></v-checkbox>
            </div>

            <v-list dense>
              <v-list-item
                v-for="trip in group.trips"
                :key="trip.id"
                class="trip-item"
              >
                <v-list-item-action>
                  <v-checkbox
                    v-model="selectedTrips"
                    :value="trip.id"
                    hide-details
                    @change="updateGroupSelection(group)"
                  ></v-checkbox>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>
                    <v-icon small class="mr-2">mdi-clock-outline</v-icon>
                    {{ formatTime(trip.fromTime) }} -
                    {{ formatTime(trip.toTime) }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <v-icon small class="mr-1">mdi-map-marker</v-icon>
                    <span v-if="trip.route">
                      {{ trip.route.name }}
                    </span>
                    <span v-else>
                      {{ trip.fromLocation.name }} → {{ trip.toLocation.name }}
                    </span>
                    <v-chip
                      x-small
                      class="ml-2"
                      :color="getBookingStatusColor(trip)"
                    >
                      {{ trip.booked || 0 }}/{{
                        trip.capacity || trip.boat.capacity
                      }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <v-chip
                    x-small
                    :color="trip.departed ? 'success' : 'grey'"
                    text-color="white"
                  >
                    {{ trip.departed ? "Departed" : "Pending" }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <v-divider
              v-if="index < groupedTrips.length - 1"
              class="my-3"
            ></v-divider>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn outlined @click="closeDialog">Cancel</v-btn>
        <v-btn
          color="error"
          :disabled="selectedTrips.length === 0"
          :loading="deleting"
          @click="confirmDelete"
        >
          Delete Selected ({{ selectedTrips.length }})
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="confirmDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete {{ selectedTrips.length }} trip(s)?
          This action cannot be undone.
          <v-alert v-if="selectedTripsHaveBookings" type="warning" dense text>
            <strong>WARNING:</strong> Selected trips contain booked trips
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="deleteSelectedTrips">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "BoatTripsDialog",
  props: {
    value: Boolean,
    boatId: Number,
    boatName: String,
    message: Function,
    errorMessage: Function,
  },
  data: () => ({
    dialog: false,
    loading: false,
    deleting: false,
    confirmDialog: false,
    trips: [],
    selectedTrips: [],
    groupedTrips: [],
  }),
  computed: {
    totalTripsCount() {
      return this.trips.length;
    },
    unbookedTripsCount() {
      return this.trips.filter((trip) => (trip.booked || 0) === 0).length;
    },
    selectedTripsHaveBookings() {
      return this.trips.some(
        (trip) => this.selectedTrips.includes(trip.id) && (trip.booked || 0) > 0
      );
    },
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val && this.boatId) {
        this.fetchTrips();
      }
    },
    dialog(val) {
      this.$emit("input", val);
      if (!val) {
        this.resetData();
      }
    },
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    resetData() {
      this.trips = [];
      this.selectedTrips = [];
      this.groupedTrips = [];
      this.confirmDialog = false;
    },
    fetchTrips() {
      if (!this.boatId) return;

      this.loading = true;
      axios({
        url: `${
          process.env.VUE_APP_BASE_URL ?? ""
        }/api/gettripsforboat?boatId=${this.boatId}`,
        method: "GET",
        headers: { Authorization: localStorage.getItem("Authorization") },
      })
        .then((resp) => {
          this.trips = (resp.data.trips || []).filter((trip) => !trip.departed);
          this.groupTripsByDate();
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          } else {
            this.errorMessage("Failed to fetch trips");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    groupTripsByDate() {
      const grouped = {};

      this.trips.forEach((trip) => {
        const date = moment(trip.fromTime).format("dddd, D MMMM YYYY");
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(trip);
      });

      this.groupedTrips = Object.keys(grouped).map((date) => ({
        date,
        trips: grouped[date].sort((a, b) =>
          moment(a.fromTime).isBefore(moment(b.fromTime)) ? -1 : 1
        ),
        allSelected: false,
      }));
    },
    formatTime(time) {
      return moment(time).format("HH:mm");
    },
    getBookingStatusColor(trip) {
      const booked = trip.booked || 0;
      const capacity = trip.capacity || trip.boat.capacity;
      const percentage = (booked / capacity) * 100;

      if (percentage >= 90) return "error";
      if (percentage >= 70) return "warning";
      return "success";
    },
    selectAll() {
      this.selectedTrips = this.trips.map((trip) => trip.id);
      this.updateAllGroupSelections();
    },
    deselectAll() {
      this.selectedTrips = [];
      this.updateAllGroupSelections();
    },
    selectUnbooked() {
      const unbookedTripIds = this.trips
        .filter((trip) => (trip.booked || 0) === 0)
        .map((trip) => trip.id);

      this.selectedTrips = [
        ...new Set([...this.selectedTrips, ...unbookedTripIds]),
      ];
      this.updateAllGroupSelections();
    },
    deselectBooked() {
      const bookedTripIds = this.trips
        .filter((trip) => (trip.booked || 0) > 0)
        .map((trip) => trip.id);

      this.selectedTrips = this.selectedTrips.filter(
        (id) => !bookedTripIds.includes(id)
      );
      this.updateAllGroupSelections();
    },
    toggleGroupSelection(group) {
      const groupTripIds = group.trips.map((trip) => trip.id);

      if (group.allSelected) {
        groupTripIds.forEach((id) => {
          if (!this.selectedTrips.includes(id)) {
            this.selectedTrips.push(id);
          }
        });
      } else {
        this.selectedTrips = this.selectedTrips.filter(
          (id) => !groupTripIds.includes(id)
        );
      }
    },
    updateGroupSelection(group) {
      const groupTripIds = group.trips.map((trip) => trip.id);
      const selectedInGroup = this.selectedTrips.filter((id) =>
        groupTripIds.includes(id)
      );
      group.allSelected = selectedInGroup.length === groupTripIds.length;
    },
    updateAllGroupSelections() {
      this.groupedTrips.forEach((group) => {
        this.updateGroupSelection(group);
      });
    },
    confirmDelete() {
      this.confirmDialog = true;
    },
    deleteSelectedTrips() {
      if (this.selectedTrips.length === 0) return;

      this.deleting = true;
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/deletetrips`,
        method: "POST",
        headers: { Authorization: localStorage.getItem("Authorization") },
        data: JSON.stringify({ tripIds: this.selectedTrips }),
      })
        .then((resp) => {
          this.message(resp.data.message);
          this.confirmDialog = false;
          this.$emit("trips-deleted");
          this.closeDialog();
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          } else {
            this.errorMessage("Failed to delete trips");
          }
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>

<style scoped>
.trip-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  transition: background-color 0.2s;
}

.trip-item:hover {
  background-color: #f5f5f5;
}
</style>
